'use client';

import React, { useEffect, useState } from 'react';
import { useDigitalCardState } from 'src/components/_common/DigitalCard/_hooks/useDigitalCard';

import PlayerInfoTextStyled from './index.styled';

interface PlayerInfoTextProps {
  playerNumber?: string;
  name: string;
  paddingBottom: number;
  fontFamily: string;
  fontWeight: number;
  textColor?: string;
  textShadow?: string;
  numberFontSize?: number;
  numberLineHeight?: number;
  nameFontSize: number;
  nameLineHeight?: number;
}

/**
 * [선수 정보 텍스트 컴포넌트]
 *
 * @description
 * 선수 정보 텍스트를 렌더링하는 컴포넌트입니다.
 * 이미지 로드 전에는 렌더링되지 않습니다.
 *
 * @param props
 * ```ts
 * {
 *   playerNumber?: string, // 선수 번호
 *   name: string, // 선수 이름
 *   paddingBottom: number, // 텍스트 아래 여백
 *   fontFamily: string, // 폰트 패밀리
 *   fontWeight: number, // 폰트 두께
 *   textShadow: string, // 텍스트 그림자
 *   numberFontSize: number, // 번호 폰트 크기
 *   numberLineHeight?: number, // 번호 줄 높이
 *   nameFontSize: number, // 이름 폰트 크기
 *   nameLineHeight?: number, // 이름 줄 높이
 * }
 * ```
 */
const PlayerInfoText = ({
  playerNumber,
  name,
  paddingBottom,
  fontFamily,
  fontWeight,
  textColor,
  textShadow,
  numberFontSize,
  numberLineHeight,
  nameFontSize,
  nameLineHeight,
}: PlayerInfoTextProps) => {
  const { isAllResourcesLoaded } = useDigitalCardState();
  const [windowScale, setWindowScale] = useState(1);

  const [firstName, ...rest] = name.split(' ');
  const lastName = rest.join(' ');

  const playerInfoTexts = {
    number: playerNumber && playerNumber !== '-' ? playerNumber : undefined,
    name: [firstName, lastName],
  };

  let charIndex = 0;

  /**
   * 문자 렌더링
   */
  const renderChars = (text: string) => {
    return text.split('').map((char, index) => {
      const currentIndex = charIndex++;
      const delay = `${currentIndex * 0.2}s`;

      return (
        <PlayerInfoTextStyled.CharWrapper key={index}>
          {textShadow && (
            <PlayerInfoTextStyled.ShadowChar $textShadow={textShadow} style={{ animationDelay: delay }}>
              {char}
            </PlayerInfoTextStyled.ShadowChar>
          )}
          <PlayerInfoTextStyled.MainChar $textColor={textColor} style={{ animationDelay: delay }}>
            {char}
          </PlayerInfoTextStyled.MainChar>
        </PlayerInfoTextStyled.CharWrapper>
      );
    });
  };

  /**
   * 윈도우 크기에 따른 스케일링 계산
   */
  useEffect(() => {
    const calculateScale = () => {
      const windowWidth = window.innerWidth;
      if (windowWidth <= 340) {
        const newScale = (windowWidth - 40) / 300; // 20px 마진 양쪽
        setWindowScale(newScale);
      } else {
        setWindowScale(1);
      }
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  if (!isAllResourcesLoaded) {
    return null;
  }

  return (
    <PlayerInfoTextStyled.Container $scale={windowScale} $paddingBottom={paddingBottom}>
      {playerInfoTexts.number && numberFontSize && (
        <PlayerInfoTextStyled.Number
          $scale={windowScale}
          $fontFamily={fontFamily}
          $fontWeight={fontWeight}
          $numberFontSize={numberFontSize}
          $numberLineHeight={numberLineHeight}
        >
          {renderChars(playerInfoTexts.number)}
        </PlayerInfoTextStyled.Number>
      )}
      {playerInfoTexts.name.map((name, index) => (
        <PlayerInfoTextStyled.Name
          key={index}
          $scale={windowScale}
          $fontFamily={fontFamily}
          $fontWeight={fontWeight}
          $nameFontSize={nameFontSize}
          $nameLineHeight={nameLineHeight}
        >
          {renderChars(name)}
        </PlayerInfoTextStyled.Name>
      ))}
    </PlayerInfoTextStyled.Container>
  );
};

export default PlayerInfoText;
